import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X, Layers, Shuffle, Check, AlertCircle, Download, BookmarkPlus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { decodePaletteFromShare } from '../utils/paletteCode';
import { savePalette } from '../config/savedPalettes';
import { getUserCollection } from '../utils/userCollection';
import { convertMarkersToCollection } from '../utils/colorConvert';

export default function ImportPaletteModal({ isOpen, onClose, onLoadInCreator, preloadedMarkers }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  // Step 1 state
  const [paletteCode, setPaletteCode] = useState('');
  const [decodeError, setDecodeError] = useState('');
  const [isDecoding, setIsDecoding] = useState(false);

  // Step 2 state
  const [step, setStep] = useState(1); // 1 = code input, 2 = action selection
  const [decodedMarkers, setDecodedMarkers] = useState(null);
  const [collectionMarkers, setCollectionMarkers] = useState([]);

  // Save state
  const [saveMode, setSaveMode] = useState(null); // null, 'asis', 'convert'
  const [paletteName, setPaletteName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Scroll lock
  useEffect(() => {
    if (isOpen) {
      document.documentElement.style.overflow = 'hidden';
      return () => { document.documentElement.style.overflow = ''; };
    }
  }, [isOpen]);

  // Reset all state when modal opens/closes
  useEffect(() => {
    if (isOpen && preloadedMarkers?.length) {
      // Skip step 1 — markers already decoded by caller
      setDecodedMarkers(preloadedMarkers);
      setCollectionMarkers(convertMarkersToCollection(preloadedMarkers));
      setStep(2);
    } else if (!isOpen) {
      setPaletteCode('');
      setDecodeError('');
      setIsDecoding(false);
      setStep(1);
      setDecodedMarkers(null);
      setCollectionMarkers([]);
      setSaveMode(null);
      setPaletteName('');
      setIsSaving(false);
      setSaveError('');
      setSaveSuccess(false);
    }
  }, [isOpen, preloadedMarkers]);

  // Live preview — try to decode whatever is in the textarea as the user types
  // Must be before early return to satisfy Rules of Hooks
  const livePreviewColors = useMemo(() => {
    if (step !== 1 || !paletteCode.trim()) return [];
    try {
      const decoded = decodePaletteFromShare(paletteCode.trim());
      return decoded && decoded.length > 0 ? decoded.map(m => m.hex) : [];
    } catch {
      return [];
    }
  }, [paletteCode, step]);

  if (!isOpen) return null;

  const handleDecode = async () => {
    if (!paletteCode.trim()) {
      setDecodeError('Please paste a palette code.');
      return;
    }

    setIsDecoding(true);
    setDecodeError('');

    try {
      const decoded = decodePaletteFromShare(paletteCode.trim());
      if (!decoded || decoded.length === 0) {
        setDecodeError(t('savedPalettes.errors.invalidCode'));
        setIsDecoding(false);
        return;
      }

      // Compute collection-converted markers
      const converted = convertMarkersToCollection(decoded);

      setDecodedMarkers(decoded);
      setCollectionMarkers(converted);
      setStep(2);
    } catch (err) {
      console.error('Failed to decode palette:', err);
      setDecodeError(t('savedPalettes.errors.loadFailed'));
    } finally {
      setIsDecoding(false);
    }
  };

  const handleSave = async () => {
    if (!paletteName.trim()) {
      setSaveError('Please enter a name for the palette.');
      return;
    }

    const markersToSave = saveMode === 'convert' ? collectionMarkers : decodedMarkers;

    setIsSaving(true);
    setSaveError('');

    try {
      const result = await savePalette(user.uid, markersToSave, paletteName.trim());
      if (result.success) {
        setSaveSuccess(true);
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setSaveError(result.error || 'Failed to save palette.');
      }
    } catch (err) {
      setSaveError('Failed to save palette.');
    } finally {
      setIsSaving(false);
    }
  };

  const noCollection = getUserCollection().length === 0;
  const previewColors = decodedMarkers ? decodedMarkers.slice(0, 10) : [];

  const modal = (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeOnly">
      <div
        className="absolute inset-0 z-0"
        onClick={onClose}
      />
      <div className="relative z-10 bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden animate-slideUp">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4">
          <div>
            <div className="flex items-center gap-2">
              <BookmarkPlus size={20} className="text-emerald-500" />
              <h3 className="text-lg font-bold text-slate-900">Import Palette</h3>
            </div>
            <div className="w-16 h-1 bg-gradient-to-r from-emerald-400 to-teal-400 rounded-full mt-2" />
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Step 1: Code Input */}
        {step === 1 && (
          <div className="px-6 pb-6">
            <div className="mb-5">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('savedPalettes.loadModal.codeLabel')}
              </label>
              <textarea
                value={paletteCode}
                onChange={(e) => {
                  setPaletteCode(e.target.value);
                  setDecodeError('');
                }}
                placeholder={t('savedPalettes.loadModal.codePlaceholder')}
                rows={3}
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent resize-none font-mono text-sm bg-white/80 transition-all"
                disabled={isDecoding}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.ctrlKey && !isDecoding) {
                    handleDecode();
                  }
                }}
              />
            </div>

            {/* Live hex preview bar */}
            {livePreviewColors.length > 0 && (
              <div className="flex rounded-xl overflow-hidden h-6 mb-4 shadow-sm">
                {livePreviewColors.map((hex, i) => (
                  <div key={i} className="flex-1" style={{ backgroundColor: hex }} title={hex} />
                ))}
              </div>
            )}

            {!user && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                <AlertCircle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700">
                  Saving requires a login. You can still decode and load the palette in the creator.
                </p>
              </div>
            )}

            {decodeError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{decodeError}</p>
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <button
                onClick={onClose}
                className="px-5 py-2 rounded-xl bg-white/80 hover:bg-slate-50 border border-slate-200 text-slate-600 text-xs font-medium transition-all"
                disabled={isDecoding}
              >
                Cancel
              </button>
              <button
                onClick={handleDecode}
                disabled={isDecoding || !paletteCode.trim()}
                className="px-5 py-2 rounded-xl bg-gradient-to-r from-emerald-400 to-teal-500 hover:from-emerald-500 hover:to-teal-600 text-white text-xs font-medium shadow-sm transition-all inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download size={13} />
                {isDecoding ? 'Decoding...' : 'Continue'}
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Action Selection */}
        {step === 2 && decodedMarkers && (
          <div className="px-6 pb-6">
            {/* Color strip preview */}
            <div className="flex rounded-xl overflow-hidden h-8 mb-3 shadow-sm">
              {previewColors.map((m, i) => (
                <div
                  key={i}
                  className="flex-1"
                  style={{ backgroundColor: m.hex || '#ccc' }}
                  title={m.hex}
                />
              ))}
            </div>
            <p className="text-xs text-slate-500 mb-4">
              {decodedMarkers.length} marker{decodedMarkers.length !== 1 ? 's' : ''} decoded
            </p>

            {/* Success state */}
            {saveSuccess ? (
              <div className="py-6 text-center">
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                  <Check size={24} className="text-emerald-600" />
                </div>
                <p className="text-sm font-medium text-slate-800">Saved!</p>
              </div>
            ) : (
              <>
                {saveError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                    <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{saveError}</p>
                  </div>
                )}

                {/* Name input (shown when a save mode is selected) */}
                {saveMode && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Palette name
                    </label>
                    <div className="flex gap-1 items-center">
                      <input
                        type="text"
                        value={paletteName}
                        onChange={(e) => {
                          setPaletteName(e.target.value);
                          setSaveError('');
                        }}
                        placeholder="e.g. Autumn Vibes"
                        maxLength={50}
                        autoFocus
                        className="flex-1 px-3 py-1.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm transition-all"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !isSaving && paletteName.trim()) handleSave();
                          if (e.key === 'Escape') { setSaveMode(null); setSaveError(''); }
                        }}
                        disabled={isSaving}
                      />
                      <button
                        onClick={handleSave}
                        disabled={isSaving || !paletteName.trim()}
                        className="p-1.5 text-emerald-600 hover:text-emerald-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        title="Save"
                      >
                        <Check size={18} />
                      </button>
                      <button
                        onClick={() => { setSaveMode(null); setSaveError(''); }}
                        disabled={isSaving}
                        className="p-1.5 text-slate-400 hover:text-slate-600 transition-colors"
                        title="Cancel"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  </div>
                )}

                {user && (
                  <div className="space-y-3">
                    {/* Save as-is */}
                    <button
                      onClick={() => { setSaveMode('asis'); setSaveError(''); }}
                      disabled={isSaving || saveMode === 'asis'}
                      className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all group text-left disabled:cursor-not-allowed ${
                        saveMode === 'asis'
                          ? 'border-emerald-300 bg-emerald-50'
                          : 'border-slate-100 hover:border-emerald-300 hover:bg-emerald-50 disabled:opacity-50'
                      }`}
                    >
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-400 to-slate-500 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                        <Layers size={20} className="text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-800 group-hover:text-emerald-700 transition-colors">
                          Save as-is
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                          Save all {decodedMarkers.length} original markers exactly as they appear.
                        </p>
                      </div>
                    </button>

                    {/* Convert & Save */}
                    <button
                      onClick={() => { setSaveMode('convert'); setSaveError(''); }}
                      disabled={isSaving || noCollection || collectionMarkers.length === 0 || saveMode === 'convert'}
                      className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all group text-left disabled:cursor-not-allowed ${
                        saveMode === 'convert'
                          ? 'border-purple-300 bg-purple-50'
                          : 'border-slate-100 hover:border-purple-300 hover:bg-purple-50 disabled:opacity-50'
                      }`}
                    >
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-400 to-violet-500 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                        <Shuffle size={20} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-slate-800 group-hover:text-purple-700 transition-colors">
                            Convert &amp; Save
                          </p>
                          {!noCollection && (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">
                              {collectionMarkers.length} markers
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                          {noCollection
                            ? 'Add markers to your collection first.'
                            : collectionMarkers.length === 0
                            ? 'No markers in your collection match this palette.'
                            : `Each marker matched to the closest one you own. You may get fewer than ${decodedMarkers.length} markers.`
                          }
                        </p>
                      </div>
                    </button>
                  </div>
                )}

                {/* Load in creator button */}
                {onLoadInCreator && (
                  <div className={user ? 'mt-4 pt-4 border-t border-slate-100' : 'mt-0'}>
                    <button
                      onClick={() => {
                        onLoadInCreator(decodedMarkers);
                        onClose();
                      }}
                      disabled={isSaving}
                      className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-white/80 hover:bg-slate-50 text-slate-600 text-xs font-medium transition-all inline-flex items-center justify-center gap-2"
                    >
                      <Download size={13} className="text-slate-500" />
                      Load in creator
                    </button>
                  </div>
                )}

                {!user && !onLoadInCreator && (
                  <div className="mt-4 text-center">
                    <button
                      onClick={() => navigate('/palettes')}
                      className="text-sm text-emerald-600 hover:text-emerald-700 underline transition-colors"
                    >
                      Sign in to save palettes
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
